// Configuration de l'API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

export interface RequestOtpRequest {
  email: string;
  otpType: string;
  language?: string;
}

export interface VerifyOtpRequest {
  email: string;
  otp: string;
  otpType: string;
  language?: string;
}

export interface ResetPasswordRequest {
  email: string;
  resetToken: string;
  newPassword: string;
  language?: string;
}

export interface PasswordResetResponse {
  success: boolean;
  message: string;
  code?: string;
  resetToken?: string;
}

export class ApiError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

export class PasswordResetService {
  private static instance: PasswordResetService;
  private baseUrl: string;

  private constructor() {
    this.baseUrl = API_BASE_URL;
  }

  public static getInstance(): PasswordResetService {
    if (!PasswordResetService.instance) {
      PasswordResetService.instance = new PasswordResetService();
    }
    return PasswordResetService.instance;
  }

  /**
 * Demande d'envoi du code OTP
 */
  async requestOtp(email: string, language: string = 'fr'): Promise<PasswordResetResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          otpType: 'PASSWORD_RESET',
          language
        } as RequestOtpRequest),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new ApiError(data.message || 'Erreur lors de la demande du code OTP', data.code);
      }

      return data as PasswordResetResponse;
    } catch (error) {
      console.error('Erreur lors de la demande du code OTP:', error);
      throw error;
    }
  }

  /**
   * Vérification du code OTP
   */
  async verifyOtp(email: string, otp: string, language: string = 'fr'): Promise<PasswordResetResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          otp,
          otpType: 'PASSWORD_RESET',
          language
        } as VerifyOtpRequest),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new ApiError(data.message || 'Erreur lors de la vérification du code OTP', data.code);
      }

      return data as PasswordResetResponse;
    } catch (error) {
      console.error('Erreur lors de la vérification du code OTP:', error);
      throw error;
    }
  }

  /**
   * Réinitialisation du mot de passe
   */
  async resetPassword(email: string, resetToken: string, newPassword: string, language: string = 'fr'): Promise<PasswordResetResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, resetToken, newPassword, language } as ResetPasswordRequest),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new ApiError(data.message || 'Erreur lors de la réinitialisation du mot de passe', data.code);
      }

      return data as PasswordResetResponse;
    } catch (error) {
      console.error('Erreur lors de la réinitialisation du mot de passe:', error);
      throw error;
    }
  }

  /**
   * Validation de l'email
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

export const passwordResetService = PasswordResetService.getInstance();
